import React, { useState } from 'react'
import PropTypes from 'prop-types'
import { toastr } from 'react-redux-toastr'
import _ from 'lodash'
import './Files.css'
import {Upload} from 'lucide-react'
import { defaultT } from '../utils/i18n'
import { Table, TrashCell, AutoSizer, Column } from './DataTable'
import { ProgressBar } from './ProgressBar/ProgressBar'
import axios from 'axios'
import UploadHint from './UploadHint'
import { isTooLarge } from '../utils/uploadLimits'
import moment from "moment";


const ImagesUploader = props => {

    const [uploadState, setUploadState] = useState(0)
    // update the image table by adding a row
    const handleResponse = data => {
        const { push, user } = props
        if (data) {
            push({
                ...data,
                type: 'image',
                user
            })
        }
        else {
            toastr.error(t('errorUploading'))
        }
    }

    const handleImageUpload = t => event => {
        const files = event.target.files
        let data = new FormData()
        const fileSize = files[0].size
        const chunkSize = 15 * 1024 * 1024 // 15MB
        let fileChunk = null, chunkNb = 0

        if (fileSize > 500 * 1024 * 1024) {
            toastr.error(t('tooLargeFile'))
        }
        else {
            for (let start = 0; start < fileSize; start += chunkSize, chunkNb++) {
                if (start + chunkSize > fileSize) {
                    fileChunk = files[0].slice(start, fileSize)
                }
                else {
                    fileChunk = files[0].slice(start, start + chunkSize)
                }
                data.append('imageChunks', fileChunk, files[0].name)
            }
            data.append('user', JSON.stringify(props.user))
            data.append('fileSize', fileSize)
            data.append('chunkNb', chunkNb)

            const options = {
                headers: { 'Content-Type': 'multipart/form-data' },
                onUploadProgress: (progressEvent) => {
                    const { loaded, total } = progressEvent
                    let percent = Math.floor( (loaded * 100) / total )
                    setUploadState(percent)
                }
            }
            axios.post('/image', data, options)
            .then(res => {
                handleResponse(res.data)
                toastr.success(t('successImageUploading'))
                setUploadState(0)
            })
            .catch(err => {
                const data = _.get(err, 'response.data', {})
                toastr.error(t(data.message || 'errorUploading', data))
                setUploadState(0)
            })
        }
        // Allow to re-upload the same file in a row
        event.target.value = null
    }

    const handleOneImageUpload = async (file, t) => {
        let data = new FormData()
        const fileSize = file.size
        const chunkSize = 15 * 1024 * 1024 // 15MB
        let fileChunk = null, chunkNb = 0

        if (await isTooLarge(file, 'image')) {
            return toastr.error(t('tooLargeFile'))
        }

        for (let start = 0; start < fileSize; start += chunkSize, chunkNb++) {
            if (start + chunkSize > fileSize) { fileChunk = file.slice(start, fileSize) }
            else { fileChunk = file.slice(start, start + chunkSize) }
            data.append('imageChunks', fileChunk, file.name)
        }

        data.append('user', JSON.stringify(props.user))
        data.append('fileSize', fileSize)
        data.append('chunkNb', chunkNb)

        const options = {
            headers: { 'Content-Type': 'multipart/form-data' },
            onUploadProgress: (progressEvent) => {
                const { loaded, total } = progressEvent
                const percent = Math.floor((loaded * 100) / total)
                setUploadState(percent)
            }
        }

        try {
            const res = await axios.post('/image', data, options)
            handleResponse(res.data)
            toastr.success(t('successImageUploading'))
        } catch (err) {
            const data = _.get(err, 'response.data', {})
            toastr.error(t(data.message || 'errorUploading', data))
        } finally {
            setUploadState(0)
        }
    }

    const handleImagesUpload = t => async event => {
        const files = Array.from(event.target.files) // copy the FileList early
        event.target.value = null // reset to null to allow re-uploading the same files

        for (let i = 0; i < files.length; i++) {
            await handleOneImageUpload(files[i], t)
        }
    }

    const handleClickTrash = (rowIndex, fileName, t) => () => {
        toastr.confirm(t('deletionConfirmation'), {
            onOk: () => axios.delete(`/image/${fileName}`)
                .then((res) => {
                    if(res.data.ok || res.data.code === "ENOENT") {
                        toastr.success(t('deletion'), t('successImageDeleting'))
                        props.remove(rowIndex)
                    } else {
                        toastr.error(t('deletion'), res.data.message)
                    }
                })
        })
    }
    const renderDeleteIcon = ({ rowIndex, rowData, t }) => {
        const fullDate = moment(rowData.date).format('YYYY-MM-DD_HH-mm-ss')
        const fileName = `${fullDate}_${rowData.name}`
        return <TrashCell onClick={handleClickTrash(rowIndex, fileName, t)} />
    }

    const { allIds = [], byId = {}, t = defaultT } = props
    const objects = allIds.map(id => byId[id])
    const firstColumnLabel = props.tKey
    const defaultPlaceHolder = props.fieldType === 'logoPicker' ? 'addLogo' : 'addImage(s)'
    const uploadButtonPlaceholder = props.placeholder || defaultPlaceHolder

    let display = null
    if (props.editable) {
        display = <div className="Images-Files-container">
                    <div className="Files-panel-body">
                        <AutoSizer disableHeight>
                            {({ width }) => (
                                <Table
                                    objects={objects}
                                    maxRows={7}
                                    width={width}
                                    noRowsLabel={t('noRows')}
                                >
                                    <Column
                                        flexGrow={1}
                                        width={200}
                                        dataKey="name"
                                        label={t(firstColumnLabel, { capitalize: true })}
                                        className='Image-DataTable-standard-column'
                                    />
                                    <Column
                                        flexGrow={0}
                                        width={220}
                                        dataKey="user.name"
                                        label={t('user', { capitalize: true })}
                                        cellDataGetter={({ dataKey, rowData }) => _.get(rowData, dataKey)}
                                        className='Image-DataTable-standard-column'
                                    />
                                    <Column
                                        flexGrow={0}
                                        width={160}
                                        dataKey="date"
                                        label={t('date', { capitalize: true })}
                                        className='Image-DataTable-standard-column'
                                    />
                                    <Column
                                        flexGrow={0}
                                        width={40}
                                        dataKey="id"
                                        label=""
                                        cellRenderer={(data) => renderDeleteIcon({...data, t})}
                                        className='Image-DataTable-standard-column'
                                    />
                                </Table>
                            )}
                        </AutoSizer>
                    </div>
                    <div className="Files-input-group">
                        <button className="btn Files-button">
                            <Upload size={16} className="Files-Icon" />
                            <span className="Files-button-Text">
                                {t(uploadButtonPlaceholder, { capitalize: true })}
                            </span>
                        </button>
                        <input
                            className='Files-input'
                            type='file'
                            accept='image/png, image/jpeg'
                            multiple
                            encType='multipart/form-data'
                            onChange={handleImagesUpload(t)}
                        />
                    </div>
                    <UploadHint kind="image" t={t} />
                    { uploadState > 0 ?
                        <div className="uploadProgress">
                            { t('uploading') }
                            <ProgressBar percentage={uploadState} color='#E94E24'/>
                            {uploadState}%
                        </div>
                        : null
                    }
                </div>
    }
    return display
}

ImagesUploader.propTypes = {
    push: PropTypes.func.isRequired,
    remove: PropTypes.func.isRequired,
    t: PropTypes.func
}

export default ImagesUploader
